/*
 * 로그인에 쓰는 값과, 로그인 화면이 쓰는 자리 둘.
 *
 * **Entra 값이 여기 있다.** 화면이 제 출처에서 MSAL 을 직접 돌린다(auth/broker.ts). 한동안은
 * `auth.sfood.ai` 가 이 값들을 들고 화면은 토큰만 받아 왔는데, 그 사이트가 아직 뜨지 않아
 * 로그인을 이 저장소로 되돌렸다. 되돌아갈 때 고칠 자리는 broker.ts 의 구현 하나다.
 *
 * 셋 다 비밀이 아니다. 공개 클라이언트라 시크릿이 없고, 이 값들은 로그인 요청의 주소에 그대로
 * 실려 나간다. 비밀은 사람이 로그인해서 받는 토큰 쪽에 있다.
 */

/** SPA 공개 클라이언트. 시크릿이 없다. */
export const ENTRA_CLIENT_ID = '1db5cdb1-33d1-41d8-96cd-858db32ee9d7';

export const ENTRA_TENANT_ID = '4ae6d665-9b9c-4774-91da-84a3fb10dce1';

export const ENTRA_AUTHORITY = `https://login.microsoftonline.com/${ENTRA_TENANT_ID}`;

/**
 * 받으려는 범위. Graph `User.Read` 만 받으면 토큰의 `aud` 가 Graph 가 되어 우리 API 가 못
 * 받는다.
 *
 * **서버가 보는 이름과 글자까지 같아야 한다**(`.env` 의 `ENTRA_REQUIRED_SCOPE`). 사내 앱들이
 * 청중이 되는 등록 `fd9d3c28-…` 를 공용으로 쓰기로 해서 다른 서비스의 토큰이 우리와 같은
 * `aud` 를 달고, 그래서 **범위 이름이 두 서비스를 가르는 유일한 경계**다. 여기서 rooms 의
 * 이름을 받아 오면 서버가 401 로 거절한다.
 *
 * v2 액세스 토큰의 `aud` 는 이 URI 가 아니라 API 앱 ID(GUID)다. 서버가 둘 다 받는다.
 */
export const ENTRA_API_SCOPE = 'api://fd9d3c28-0d1a-472f-b1dc-5c389e97ab2b/access_as_user';

/**
 * 로그인 팝업이 돌아오는 자리. **Entra 에 SPA 리디렉션 URI 로 등록된 주소여야 한다.**
 *
 * 화면을 내지 않는 빈 문서다(`apps/web/public/auth-done.html`). 앱의 `index.html` 을 쓰면 그
 * 팝업 안에서 React 와 라우터가 통째로 한 번 뜨고 곧 닫힌다 — 느리고, 그 찰나에 가드가 돌아
 * 로그인 화면을 그리려 든다. MSAL 은 이 창의 주소에서 코드를 읽어 가므로 문서가 비어 있어도
 * 된다.
 *
 * 로그아웃 팝업도 같은 자리로 돌아온다. 등록할 주소를 하나로 두려는 것이다.
 */
export const REDIRECT_PATH = '/auth-done.html';

/**
 * 빈 값을 "안 적은 것" 으로 본다.
 *
 * `??` 로는 모자라다. `.env` 에 이름만 적고 값을 비워 두면 빈 문자열이 들어오고, 그것은
 * `undefined` 가 아니라 통과해서 기본값을 지운다.
 */
const setting = (value: string | undefined, fallback: string): string =>
  value !== undefined && value.trim() !== '' ? value.trim() : fallback;

/** 개발에서 지어낼 사람. [LOCAL_AUTH] 일 때만 쓰인다. */
export const LOCAL_USER = {
  email: setting(import.meta.env.LOCAL_USER_EMAIL, 'dev@s-food.com'),
  name: setting(import.meta.env.LOCAL_USER_NAME, '개발자'),
} as const;

/**
 * 개발에서 로그인을 건너뛴다. 서버의 `LOCAL_AUTH` 와 **같은 환경변수**다.
 *
 * 두 자리로 나누면 한쪽만 켠 채로 화면이 지어낸 토큰을 서버가 거절해 전부 401 이 된다.
 *
 * 개발에서 진짜로 로그인하려면 이 값을 끄고, `http://localhost:4200/auth-done.html` 을 위
 * 클라이언트의 SPA 리디렉션 URI 에 더한다.
 */
export const LOCAL_AUTH = import.meta.env.LOCAL_AUTH === 'true';

/*
 * 돌아갈 자리를 못 믿을 때 가는 곳. 첫 화면이 어디인지는 라우터의 `/` 하나만 안다(routes/index.tsx).
 * 여기에 화면 주소를 박으면 첫 화면을 옮길 때 고칠 자리가 둘이 된다.
 */
const HOME = '/';

/** 로그인 화면의 주소. 만드는 자리가 셋이라 문자열을 여기 모은다. */
export const LOGIN_PATH = '/login';

/**
 * 로그인 뒤 돌아갈 주소. 디코드한 뒤에도 같은 출처 경로만 받는다.
 *
 * `//host` 와 `%2F%2F` 우회를 막지 않으면 로그인 화면이 다른 사이트로 사람을 넘긴다.
 */
export const safeReturnUrl = (raw: string | null | undefined): string => {
  if (!raw) return HOME;
  let value = raw.trim();
  for (let round = 0; round < 5; round += 1) {
    let next: string;
    try {
      next = decodeURIComponent(value);
    } catch {
      return HOME;
    }
    if (next === value) break;
    value = next;
  }
  value = value.replaceAll('\\', '/').trim();
  if (hasControlChars(value) || value.length > 2048) return HOME;
  if (!value.startsWith('/') || value.startsWith('//')) return HOME;
  const path = value.split(/[?#]/, 1)[0] ?? value;
  if (path.includes('//')) return HOME;
  return value;
};

const hasControlChars = (value: string): boolean => {
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    if (code < 32 || code === 127) return true;
  }
  return false;
};

/**
 * 토큰을 얻지 못해 요청을 보내지도 못한 오류. 다시 로그인해야 풀린다.
 *
 * 서버가 돌려준 401 과 굳이 나누는 것은 이 오류에 HTTP 응답이 아예 없어서다. 응답 상태를 보고
 * 사유를 정하는 자리가 이 타입을 못 알아보면 로그인이 만료된 사람에게 서버 장애를 알리게 된다.
 */
export class AuthRequiredError extends Error {
  override readonly name = 'AuthRequiredError';
}
